// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { areAddressesTheSame } from "@insite/client-framework/Common/CustomerSelectors";
import { makeHandlerChainAwaitable, replaceInChain } from "@insite/client-framework/HandlerCreator";
import { createShipTo, updateBillTo, updateShipTo } from "@insite/client-framework/Services/CustomersService";
import { FulfillmentMethod } from "@insite/client-framework/Services/SessionService";
import { getBillToState } from "@insite/client-framework/Store/Data/BillTos/BillTosSelectors";
import loadShipTo from "@insite/client-framework/Store/Data/ShipTos/Handlers/LoadShipTo";
import { getShipToState } from "@insite/client-framework/Store/Data/ShipTos/ShipTosSelectors";
import {
    AddOrUpdateShipTo,
    chain,
    UpdateBillTo,
} from "@insite/client-framework/Store/Pages/OnePageCheckout/Handlers/SaveShipping";
import { BillToModel, ShipToModel } from "@insite/client-framework/Types/ApiModels";

export const XngUpdateBillTo: typeof UpdateBillTo = async props => {
    const {
        getState,
        apiParameter: { cart },
    } = props;
    const {
        context: {
            session: { fulfillmentMethod },
        },
        pages: {
            onePageCheckout: { shippingAddressFormState, billingAddressFormState, isBillingAddressTheSameAsShipping },
        },
    } = getState();
    if (!billingAddressFormState) {
        return;
    }

    const localModifiedShipTo = shippingAddressFormState?.address;
    const localModifiedBillTo = { ...billingAddressFormState.address };
    if (localModifiedShipTo && isBillingAddressTheSameAsShipping && fulfillmentMethod === FulfillmentMethod.Ship) {
        // XNG-Changes: use updateShipToWithBillToFields custom mapper
        (props as any).xlocalModifiedShipTo = {
            ...localModifiedShipTo,
            ...updateShipToWithBillToFields(localModifiedBillTo),
        };
    } else {
        (props as any).xlocalModifiedShipTo = {
            ...localModifiedShipTo,
        };
    }
    const billTo = getBillToState(getState(), cart.billToId).value;
    if (!billTo) {
        throw new Error("There was no billTo loaded for the current cart");
    }

    if (areAddressesTheSame(billTo, localModifiedBillTo)) {
        props.updatedBillTo = billTo;
    } else {
        const updatedBillTo = await updateBillTo({ billTo: localModifiedBillTo, isEdit: false });
        props.updatedBillTo = { ...updatedBillTo, validation: billTo.validation };
        props.dispatch({
            type: "Pages/OnePageCheckout/SetBillingAddress",
            address: props.updatedBillTo,
        });
    }

    props.apiParameter.cart.billToId = props.updatedBillTo.id;
};

const updateShipToWithBillToFields = (billTo: BillToModel) => {
    return {
        firstName: billTo.firstName,
        lastName: billTo.lastName,
        attention: billTo.attention,
        companyName: billTo.companyName,
        address1: billTo.address1,
        address2: billTo.address2,
        address3: billTo.address3,
        address4: billTo.address4,
        country: billTo.country,
        city: billTo.city,
        state: billTo.state,
        postalCode: billTo.postalCode,
        phone: billTo.phone,
        email: billTo.email,
        fax: billTo.fax,
        contactFullName: billTo.contactFullName,
        fullAddress: billTo.fullAddress,
    };
};

export const xAddOrUpdateShipTo: typeof AddOrUpdateShipTo = async props => {
    const { dispatch, getState, cart } = props;
    const state = getState();
    const {
        context: {
            session: { fulfillmentMethod },
        },
        pages: {
            onePageCheckout: { shippingAddressFormState, isBillingAddressTheSameAsShipping },
        },
    } = state;
    if (!shippingAddressFormState || fulfillmentMethod !== FulfillmentMethod.Ship) {
        return;
    }

    const shipTo = getShipToState(state, cart.shipToId).value;
    const billTo = getBillToState(state, cart.billToId).value;
    if (!shipTo || !billTo) {
        throw new Error("There was no shipTo or billTo loaded for the current cart");
    }

    // XNG-Change
    const localModifiedShipTo = (props as any).xlocalModifiedShipTo;
    if (isBillingAddressTheSameAsShipping && cart.shipToId === cart.billToId) {
        props.dispatch({
            type: "Pages/OnePageCheckout/SetShippingAddress",
            address: localModifiedShipTo,
        });
        return;
    }

    if (isBillingAddressTheSameAsShipping && cart.shipToId !== cart.billToId && cart.billToId) {
        props.apiParameter.cart.shipToId = cart.billToId;
        const { shipToId } = props.apiParameter.cart;
        await makeHandlerChainAwaitable(loadShipTo)({ shipToId, billToId: cart.billToId, expand: ["validation"] })(
            dispatch,
            getState,
        );
        const shipToState = getShipToState(getState(), shipToId);
        shipToState.value &&
            props.dispatch({
                type: "Pages/OnePageCheckout/SetShippingAddress",
                address: shipToState.value,
            });
        return;
    }

    let updatedShipTo;
    if (!isBillingAddressTheSameAsShipping && cart.shipToId === cart.billToId) {
        updatedShipTo = await createShipTo({ shipTo: localModifiedShipTo });
    } else if (!areAddressesTheSame(shipTo, localModifiedShipTo)) {
        updatedShipTo = await updateShipTo({ shipTo: localModifiedShipTo, billToId: billTo.id });
    }

    if (updatedShipTo) {
        props.updatedShipTo = { ...updatedShipTo, validation: shipTo.validation };
        dispatch(loadShipTo({ shipToId: props.updatedShipTo.id, billToId: billTo.id }));
        props.dispatch({
            type: "Pages/OnePageCheckout/SetShippingAddress",
            address: props.updatedShipTo,
        });
        props.apiParameter.cart.shipToId = props.updatedShipTo.id;
    }
};

replaceInChain(chain, UpdateBillTo, XngUpdateBillTo);
replaceInChain(chain, AddOrUpdateShipTo, xAddOrUpdateShipTo);
